import {
  FilePreviewDescriptor,
  FilePreviewFile,
  FilePreviewKind,
} from "../types";
import {
  appendFileNameToMediaDownloadUrl,
  buildMediaDownloadUrl,
} from "../../../utils/mediaDownloadUrl";

const PDF_EXTENSIONS = new Set(["pdf"]);
const DOCX_EXTENSIONS = new Set(["docx"]);
const SPREADSHEET_EXTENSIONS = new Set(["xlsx", "xls", "xlsm", "csv"]);
const IMAGE_EXTENSIONS = new Set([
  "avif",
  "bmp",
  "gif",
  "heic",
  "heif",
  "ico",
  "jfif",
  "png",
  "jpg",
  "jpeg",
  "webp",
  "svg",
  "tif",
  "tiff",
]);
const TEXT_EXTENSIONS = new Set([
  "txt",
  "md",
  "json",
  "xml",
  "html",
  "htm",
  "log",
  "yaml",
  "yml",
]);

const PDF_MIME_TYPES = new Set(["application/pdf"]);
const DOCX_MIME_TYPES = new Set([
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
]);
const SPREADSHEET_MIME_TYPES = new Set([
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
  "text/csv",
]);
const IMAGE_MIME_PREFIX = "image/";
const TEXT_MIME_PREFIXES = ["text/", "application/json"];
const ZIP_SIGNATURE = [0x50, 0x4b];
const PDF_SIGNATURE = [0x25, 0x50, 0x44, 0x46, 0x2d];
const PDF_SIGNATURE_SEARCH_LENGTH = 64;

function decodeFileName(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch (_error) {
    return value;
  }
}

export function normalizeContentType(contentType?: string | null): string {
  return String(contentType || "")
    .split(";")[0]
    .trim()
    .toLowerCase();
}

export function resolveFileNameFromHeaders(headers: any): string {
  const contentDisposition =
    headers?.["content-disposition"] || headers?.["Content-Disposition"];
  if (!contentDisposition) return "";

  const utf8Match = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(contentDisposition);
  if (utf8Match?.[1]) {
    return decodeFileName(utf8Match[1].trim().replace(/^"(.*)"$/, "$1"));
  }

  const plainMatch = /filename="?([^"]+)"?/i.exec(contentDisposition);
  if (plainMatch?.[1]) {
    return plainMatch[1].trim();
  }

  return "";
}

export function resolveFileExtension(fileName?: string | null): string {
  const normalizedFileName = String(fileName || "").trim();
  const dotIndex = normalizedFileName.lastIndexOf(".");
  if (dotIndex < 0) return "";
  return normalizedFileName.slice(dotIndex + 1).toLowerCase();
}

export function resolveFilePreviewKind(params: {
  fileName?: string | null;
  contentType?: string | null;
}): FilePreviewKind {
  const extension = resolveFileExtension(params.fileName);
  const contentType = normalizeContentType(params.contentType);

  if (PDF_EXTENSIONS.has(extension) || PDF_MIME_TYPES.has(contentType)) {
    return "pdf";
  }

  if (DOCX_EXTENSIONS.has(extension) || DOCX_MIME_TYPES.has(contentType)) {
    return "docx";
  }

  if (
    SPREADSHEET_EXTENSIONS.has(extension) ||
    SPREADSHEET_MIME_TYPES.has(contentType)
  ) {
    return "spreadsheet";
  }

  if (
    IMAGE_EXTENSIONS.has(extension) ||
    contentType.startsWith(IMAGE_MIME_PREFIX)
  ) {
    return "image";
  }

  if (
    TEXT_EXTENSIONS.has(extension) ||
    TEXT_MIME_PREFIXES.some((prefix) => contentType.startsWith(prefix))
  ) {
    return "text";
  }

  return "unsupported";
}

export function isImageFileName(fileName?: string | null): boolean {
  return IMAGE_EXTENSIONS.has(resolveFileExtension(fileName));
}

export function buildFilePreviewFile(params: {
  fileId: string;
  blob: Blob;
  headers?: any;
}): FilePreviewFile {
  const headerFileName = resolveFileNameFromHeaders(params.headers);
  const contentType = normalizeContentType(
    params.headers?.["content-type"] ||
      params.headers?.["Content-Type"] ||
      params.blob.type
  );
  const fallbackExtension =
    contentType === "application/pdf"
      ? "pdf"
      : contentType ===
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
      ? "docx"
      : contentType ===
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        contentType === "application/vnd.ms-excel"
      ? "xlsx"
      : "";
  const fileName =
    headerFileName || `file-${params.fileId}${fallbackExtension ? `.${fallbackExtension}` : ""}`;
  const extension = resolveFileExtension(fileName);

  return {
    fileId: params.fileId,
    fileName,
    contentType,
    extension,
    kind: resolveFilePreviewKind({ fileName, contentType }),
    blob: params.blob,
  };
}

export function toFilePreviewDescriptor(
  file: Pick<
    FilePreviewFile,
    "fileId" | "fileName" | "contentType" | "extension" | "kind"
  >,
): FilePreviewDescriptor {
  return {
    fileId: file.fileId,
    fileName: file.fileName,
    contentType: file.contentType,
    extension: file.extension,
    kind: file.kind,
  };
}

export function buildFilePreviewDescriptor(params: {
  fileId: string;
  fileName?: string | null;
  contentType?: string | null;
}): FilePreviewDescriptor {
  const fileId = String(params.fileId).trim();
  const fileName = String(params.fileName || "")
    .trim()
    .replace(/^"(.*)"$/, "$1");
  const contentType = normalizeContentType(params.contentType);
  const extension = resolveFileExtension(fileName);

  return {
    fileId,
    fileName,
    contentType,
    extension,
    kind: resolveFilePreviewKind({ fileName, contentType }),
  };
}

async function readBlobSignature(blob: Blob, length: number): Promise<Uint8Array> {
  const chunk = blob.slice(0, length);

  if (typeof chunk.arrayBuffer === "function") {
    const buffer = await chunk.arrayBuffer();
    return new Uint8Array(buffer);
  }

  const buffer = await new Promise<ArrayBuffer>((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      resolve(reader.result as ArrayBuffer);
    };
    reader.onerror = () => {
      reject(reader.error || new Error("Không thể đọc dữ liệu tệp."));
    };

    reader.readAsArrayBuffer(chunk);
  });

  return new Uint8Array(buffer);
}

function matchesSignature(bytes: Uint8Array, signature: number[]): boolean {
  if (bytes.length < signature.length) return false;
  return signature.every((value, index) => bytes[index] === value);
}

function containsSignature(bytes: Uint8Array, signature: number[]): boolean {
  if (bytes.length < signature.length) return false;

  for (let index = 0; index <= bytes.length - signature.length; index += 1) {
    if (signature.every((value, offset) => bytes[index + offset] === value)) {
      return true;
    }
  }

  return false;
}

function looksLikeTextContent(contentType: string): boolean {
  return TEXT_MIME_PREFIXES.some((prefix) => contentType.startsWith(prefix));
}

export async function normalizeFilePreviewFile(
  file: FilePreviewFile,
): Promise<FilePreviewFile> {
  if (file.kind === "docx" || file.kind === "spreadsheet") {
    const signature = await readBlobSignature(file.blob, ZIP_SIGNATURE.length);
    if (matchesSignature(signature, ZIP_SIGNATURE)) {
      return file;
    }

    return {
      ...file,
      kind: looksLikeTextContent(file.contentType) ? "text" : "unsupported",
    };
  }

  if (file.kind === "pdf") {
    const signature = await readBlobSignature(
      file.blob,
      PDF_SIGNATURE_SEARCH_LENGTH
    );
    if (containsSignature(signature, PDF_SIGNATURE)) {
      return file;
    }

    return {
      ...file,
      kind: looksLikeTextContent(file.contentType) ? "text" : "unsupported",
    };
  }

  return file;
}

export function downloadBlobFile(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

function getMediaBaseUrl(): string {
  const configuredBaseUrl = process.env.REACT_APP_API_MEDIA_BASE_URL?.trim();
  if (configuredBaseUrl) {
    return configuredBaseUrl.replace(/\/+$/, "");
  }

  return window.location.origin;
}

function getOfficePreviewBaseUrl(): string | null {
  const configuredPreviewBaseUrl =
    process.env.REACT_APP_FILE_PREVIEW_DOMAIN?.trim();
  if (configuredPreviewBaseUrl) {
    return configuredPreviewBaseUrl.replace(/\/+$/, "");
  }

  return null;
}

export function buildFileDownloadUrl(
  fileId: string,
  fileName?: string | null,
): string {
  return buildMediaDownloadUrl({
    fileId,
    fileName,
    baseUrl: getMediaBaseUrl(),
  });
}

function buildOfficePreviewFileDownloadUrl(
  fileId: string,
  fileName?: string | null,
): string {
  const officePreviewBaseUrl = getOfficePreviewBaseUrl();
  if (!officePreviewBaseUrl) {
    return buildFileDownloadUrl(fileId, fileName);
  }

  const baseDownloadUrl = new URL(
    `/media/api/files/download/${String(fileId).trim()}`,
    officePreviewBaseUrl,
  ).toString();

  return appendFileNameToMediaDownloadUrl(baseDownloadUrl, fileName);
}

export function buildOfficePreviewUrl(
  fileId: string,
  fileName?: string | null,
): string {
  const downloadUrl = buildOfficePreviewFileDownloadUrl(fileId, fileName);
  return `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(downloadUrl)}`;
}

export function buildGooglePreviewUrl(
  fileId: string,
  fileName?: string | null,
): string {
  const downloadUrl = buildFileDownloadUrl(fileId, fileName);
  return `https://drive.google.com/viewerng/viewer?url=${encodeURIComponent(downloadUrl)}`;
}

export function supportsOfficeWebPreview(kind: FilePreviewKind): boolean {
  return kind === "pdf" || kind === "docx" || kind === "spreadsheet";
}

export function resolvePreviewKindLabel(kind: FilePreviewKind): string {
  switch (kind) {
    case "pdf":
      return "PDF";
    case "docx":
      return "DOCX";
    case "spreadsheet":
      return "Spreadsheet";
    case "image":
      return "Image";
    case "text":
      return "Text";
    default:
      return "File";
  }
}
